/**
 * ContextNoAccess — full-page state shown when a ctx-param switch is refused
 * (#404; Q-506 card 1571). Renders in the content pane (inside the shell).
 *
 *   - 403/404 → "You don't have access to that organization|network" and one
 *     "Go to my dashboard" (the dialog primary). A retry cannot change a refusal.
 *   - anything else → "Couldn't open that organization|network", the quiet
 *     "Try again" under the title (RegionLoadState's control), then the
 *     "Go to my dashboard" dialog primary below it.
 *
 * The title block is the page's alert. Focus lands on "Try again" (failed) or
 * "Go to my dashboard" (no access) when the page appears. The server's error
 * reference is not shown (it still goes to the crash report).
 */

import { useEffect, useRef } from 'react';
import { Text } from '@/components/primitives';
import { ActionButton } from '@/components/common/ActionButton';
import { RegionTryAgain } from '@/components/feedback/RegionLoadState';
import { Actions, Icons, vars } from '@/theme';
import { testIds } from '@/test/testIds';
import { ctxGateMessages } from '@/constants/ctxGateMessages';

export interface ContextNoAccessProps {
  /** The context kind that was requested. */
  kind: 'org' | 'network';
  /** HTTP status of the failure (extracted by the caller). */
  status: number | null;
  /** Retry: re-issues the PUT. */
  onRetry: () => void;
  /** Go home: navigates to the user's default route. */
  onGoHome: () => void;
}

export function ContextNoAccess({ kind, status, onRetry, onGoHome }: ContextNoAccessProps) {
  const isNoAccess = status === 403 || status === 404;
  const title = isNoAccess
    ? ctxGateMessages.noAccessTitle[kind]
    : ctxGateMessages.failedTitle[kind];

  const pageRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const target = isNoAccess ? testIds.ctxNoAccess.home : testIds.ctxNoAccess.retry;
    pageRef.current?.querySelector<HTMLElement>(`[data-testid="${target}"]`)?.focus();
  }, [isNoAccess]);

  return (
    <div
      ref={pageRef}
      data-testid={testIds.ctxNoAccess.page}
      style={{ maxWidth: 560, margin: '0 auto', padding: '48px 24px' }}
    >
      <div style={{ textAlign: 'center', marginBottom: 20 }}>
        <div role="alert">
          <Icons.errorOutline
            style={{ fontSize: 48, color: vars.color.danger.base, marginBottom: 8 }}
          />
          <Text
            variant="pageHeader"
            data-testid={testIds.ctxNoAccess.title}
            style={{ fontWeight: 600, display: 'block' }}
          >
            {title}
          </Text>
        </div>
        {!isNoAccess && <RegionTryAgain onRetry={onRetry} testId={testIds.ctxNoAccess.retry} />}
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 24 }}>
        <ActionButton
          action={Actions.home}
          mode="dialog-primary"
          label={ctxGateMessages.goHome}
          onClick={onGoHome}
          testIdOverride={testIds.ctxNoAccess.home}
        />
      </div>
    </div>
  );
}
